/**
 * Reverting a step by undoing its tools.
 *
 * "Discard and go back" and a tool card's Undo button are the same operation at
 * different scales: put the socket back to before some work happened. They were
 * not built that way. Undo goes through `undoSourceResolver` — the live command,
 * else the pre-apply snapshot captured at apply time, else the stored blob — all
 * of which are *exact* records of "the socket immediately before this tool ran".
 * The step revert instead reinstalled `meshSnapshot`'s pool entry for the target
 * step, which is a different artefact written by a different code path (the
 * debounced autosave, or the step transition), and which is empty or stale in
 * every case where those two did not run the way the revert assumed — a step
 * reached by deep link or reload, a tool applied and discarded inside the
 * autosave's debounce window, a step whose pool entry was captured while the
 * scene was mid-swap. When it was wrong the revert silently left the applied
 * geometry on screen, because the mesh install itself "succeeded".
 *
 * So the revert now asks the undo router first, exactly as the Undo button does,
 * and only falls back to the mesh pool when the router has nothing.
 *
 * **Why oldest-first.** Every source here is absolute, not incremental: each one
 * restores a whole socket, not a delta. The pre-apply socket of the *earliest*
 * discarded apply is therefore, by definition, the socket as the target step left
 * it — restoring it removes every later apply in one swap, instead of paying a
 * full mesh replacement per tool. Later entries are only tried when an earlier
 * one has no material left, where landing one apply short still beats leaving all
 * of them in.
 *
 * **Why the live command is deliberately not used.** `resolveUndoSource` prefers
 * a tool's live command, whose `undo()` closure is only valid as the exact
 * inverse of the *last* thing that happened. Replaying those closures across a
 * multi-step rewind, out of order, is how you get a socket that matches no step
 * at all. Passing `null` starts the chain at the snapshot instead, which is
 * order-independent.
 */
import { GLOBAL_STEP_ORDER } from '../../../shared/constants/flowConfig';
import { hasUndoSnapshot } from '../undoSnapshot';
import useUndoManifestStore, { toolIdsForStep } from './undoManifestStore';
import {
  UNDO_SOURCE,
  REFRAME_COMPARE_BASELINE_TOOLS,
  resolveUndoSource,
  executeUndoSource,
} from './undoSourceResolver';

const LOG_PREFIX = '[StepRevert]';

/**
 * Every apply recorded after `targetStepId`, oldest first.
 *
 * The manifest is the record of what was applied and in what order. A step with
 * no manifest entries falls back to whichever of its tools still hold a pre-apply
 * snapshot — the same "a blob exists that this session has no manifest entry for"
 * allowance the resolver makes, so a lost manifest degrades instead of failing.
 *
 * @param {string} targetStepId
 * @returns {{ stepId: string, toolId: string }[]}
 */
export function appliesAfterStep(targetStepId) {
  const idx = GLOBAL_STEP_ORDER.indexOf(targetStepId);
  if (idx < 0) return [];

  const manifest = useUndoManifestStore.getState();
  const out = [];

  for (const stepId of GLOBAL_STEP_ORDER.slice(idx + 1)) {
    const entries = manifest.getEntriesForStep(stepId);
    if (entries.length > 0) {
      for (const entry of entries) out.push({ stepId, toolId: entry.toolId });
      continue;
    }
    for (const toolId of toolIdsForStep(stepId)) {
      if (hasUndoSnapshot(toolId)) out.push({ stepId, toolId });
    }
  }

  return out;
}

/**
 * Put the socket back to how the target step left it, by undoing the work
 * applied after it through the tool undo router.
 *
 * Must run *before* `discardCheckpointsAfter`, which deletes the snapshots,
 * manifests and stored blobs this reads.
 *
 * @param {string} targetStepId
 * @param {{ signal?: AbortSignal }} [opts]
 * @returns {Promise<{ ok: boolean, source?: string, from?: string, reason?: string }>}
 */
export async function undoAppliesAfterStep(targetStepId, { signal } = {}) {
  const applies = appliesAfterStep(targetStepId);
  if (applies.length === 0) return { ok: false, reason: 'nothing_applied' };

  let attempted = 0;

  for (const { stepId, toolId } of applies) {
    const plan = resolveUndoSource(null, toolId, stepId);
    if (plan.source === UNDO_SOURCE.NONE) continue;

    attempted += 1;
    try {
      const { ok, source } = await executeUndoSource(plan, toolId, stepId, {
        signal,
        reframeCompareBaseline: REFRAME_COMPARE_BASELINE_TOOLS.has(toolId),
      });
      if (ok) {
        console.log(
          `${LOG_PREFIX} restored the socket as ${targetStepId} left it — ` +
            `undid ${stepId}/${toolId} via ${source}`,
        );
        return { ok: true, source, from: `${stepId}/${toolId}` };
      }
    } catch (e) {
      if (e?.name === 'AbortError') return { ok: false, reason: 'aborted' };
      console.warn(`${LOG_PREFIX} undo of ${stepId}/${toolId} threw`, e);
    }

    console.warn(`${LOG_PREFIX} ${stepId}/${toolId} had no usable material — trying the next apply`);
  }

  return { ok: false, reason: attempted === 0 ? 'no_undo_material' : 'all_sources_failed' };
}
